import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { DonateComingSoonDialog } from "../components/DonateComingSoonDialog";
import { IMAGES } from "../data/content";
import { EmSwash } from "../components/EmSwash";
import { StudentIcon, HeartIcon } from "../lib/icons";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * Login — dual-door sign-in.
 *
 *   Left  — "Do you need to sign in?" guide (browsing + giving never need an account)
 *   Right — Students: email + password (first; it is the door with fields)
 *           Returning donors: one-tap Google sign-in (second, lighter weight)
 *
 * Both providers resolve through /api/auth/me, so post-login navigation is
 * unified: land on /dashboard regardless of provider.
 */

const GUIDE = [
  {
    key: "student",
    href: "#student-sign-in",
    tile: "bg-tangerine text-sand",
    Icon: StudentIcon,
    who: "Student",
    what: "Pick up your campaign draft.",
    note: "A My Rich Aunt administrator verifies enrollment through the National Student Clearinghouse.",
    testid: "login-guide-student",
  },
  {
    key: "donor",
    href: "#donor-sign-in",
    tile: "bg-cobalt text-sand",
    Icon: HeartIcon,
    who: "Returning donor",
    what: "Change or cancel a monthly gift.",
    note: null,
    testid: "login-guide-donor",
  },
];

const inputClass =
  "mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 text-base text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10";
const labelClass = "text-[13px] font-semibold uppercase tracking-[0.22em] text-olive";

export default function Login() {
  useNoIndex();
  const { user, loading, loginWithPassword, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [donateOpen, setDonateOpen] = useState(false);

  // If already signed in, hop straight to the destination
  useEffect(() => {
    if (!loading && user) {
      const to = location.state?.from || "/dashboard";
      navigate(to, { replace: true });
    }
  }, [loading, user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await loginWithPassword(email.trim(), password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="login-page" className="bg-sand">
      <Navbar
        onStart={() => navigate("/register")}
        onDonate={() => setDonateOpen(true)}
        primaryLabel="Create account"
      />

      <section className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-14 px-6 pb-24 pt-32 lg:grid-cols-2 lg:gap-24 lg:px-8 lg:pt-44">
        {/* Guide */}
        <div className="relative">
          <div
            aria-hidden
            data-testid="login-watermark"
            className="pointer-events-none absolute -inset-x-10 -inset-y-12 -z-0 opacity-[0.045] mix-blend-multiply"
            style={{
              backgroundImage: `url(${IMAGES.pattern})`,
              backgroundSize: "340px",
              backgroundRepeat: "repeat",
              maskImage: "radial-gradient(ellipse at 35% 30%, #000 30%, transparent 78%)",
              WebkitMaskImage: "radial-gradient(ellipse at 35% 30%, #000 30%, transparent 78%)",
            }}
          />
          <div className="relative">
            <h1 className="font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">
              Welcome <EmSwash>back.</EmSwash>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-olive" data-testid="login-lead">
              Browsing and giving never require an account. Sign in only if you are:
            </p>

            <ul className="mt-8 max-w-md divide-y divide-ink/10 border-y border-ink/10" data-testid="login-guide">
              {GUIDE.map(({ key, href, tile, Icon, who, what, note, testid }) => (
                <li key={key}>
                  <a
                    href={href}
                    data-testid={testid}
                    className="group flex items-start gap-4 py-5 transition-colors hover:text-tangerine"
                  >
                    <span className={`tile-clay mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">{who}</span>
                      <span className="mt-1 flex items-center gap-2 font-serif text-3xl leading-tight text-ink transition-transform duration-300 group-hover:translate-x-1">
                        {what}
                        <ArrowRight className="h-4 w-4 text-tangerine opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={2} />
                      </span>
                      {note && <span className="mt-2 block text-[13px] leading-relaxed text-olive">{note}</span>}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Doors */}
        <div className="flex flex-col gap-6">
          {/* Student form — the door with fields goes first */}
          <motion.form
            id="student-sign-in"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onSubmit={handleSubmit}
            className="scroll-mt-28 rounded-2xl border-2 border-tangerine bg-tangerine/[0.04] p-6 lg:p-8"
            data-testid="login-student-form"
          >
            <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
              For students
            </span>
            <h2 className="mt-3 font-serif text-3xl text-ink">Pick up your campaign.</h2>

            <label className="mt-6 block">
              <span className={labelClass}>Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
                data-testid="login-email-input"
                className={inputClass}
              />
            </label>

            <label className="mt-4 block">
              <span className={labelClass}>Password</span>
              <span className="relative block">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  data-testid="login-password-toggle"
                  className="absolute right-2 top-1/2 mt-1 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-olive transition-colors hover:bg-ink/5 hover:text-ink"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={2} /> : <Eye className="h-4 w-4" strokeWidth={2} />}
                </button>
              </span>
            </label>
            <div className="mt-2 flex justify-end">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[13px] font-semibold text-ink underline underline-offset-4 hover:text-tangerine">
                Forgot your password?
              </Link>
            </div>

            {error && (
              <p role="alert" data-testid="login-error" className="mt-4 text-[13px] text-scarlet">
                {error}
              </p>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                  </>
                ) : (
                  <>Sign in <ArrowRight className="h-4 w-4" /></>
                )}
              </button>
              <p className="text-[13px] text-olive">
                New here?{" "}
                <Link to="/register" data-testid="login-register-link" className="font-semibold text-ink underline underline-offset-4 hover:text-tangerine">
                  Create a student account
                </Link>
              </p>
            </div>
          </motion.form>

          {/* Donor pane — secondary, lighter weight */}
          <motion.div
            id="donor-sign-in"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="scroll-mt-28 rounded-2xl border border-ink/10 p-6 transition-colors hover:border-cobalt/60 focus-within:border-cobalt/60 lg:p-8"
            data-testid="login-donor-pane"
          >
            <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-cobalt">
              For returning donors
            </span>
            <h2 className="mt-3 font-serif text-3xl text-ink">Manage your monthly gift.</h2>
            <p className="mt-3 text-base leading-relaxed text-olive">
              Only needed to change or cancel a recurring donation you've already set up. First-time donors don't need an account.
            </p>
            <button
              type="button"
              onClick={loginWithGoogle}
              data-testid="login-google-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-cobalt px-6 py-3 text-base font-semibold text-cobalt transition-colors hover:bg-cobalt hover:text-sand sm:w-auto"
            >
              Continue with Google <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </motion.div>
        </div>
      </section>

      <Footer variant="minimal" />
      <DonateComingSoonDialog open={donateOpen} onOpenChange={setDonateOpen} />
    </main>
  );
}
